import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { joinInfoAppOnboardingJoinInfoGet } from '@/client/sdk.gen'
import type { JoinInfoResponse } from '@/client/types.gen'
import type { Region } from '@/hooks/use-onboarding'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/onboarding/join')({
  component: JoinLanding,
})

type ErrorState = 'token_expired' | 'token_invalid' | 'already_verified' | 'no_token' | 'unknown'

function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(globalThis.location.search)
  return params.get('token')
}

function JoinLanding() {
  useOnboardingStepViewed('join')
  const navigate = useNavigate()
  const { updateState } = useOnboarding()
  const [token] = useState(getTokenFromUrl)
  const [error, setError] = useState<ErrorState | undefined>(token ? undefined : 'no_token')

  const hydrateAndRedirect = useCallback((t: string, data: JoinInfoResponse) => {
    updateState({
      onboardingMode: 'member',
      inviteToken: t,
      email: data.email,
      company: {
        name: data.organizationName,
        domain: '',
        businessLogo: undefined,
        businessDescription: undefined,
        numberOfEmployeesRange: undefined,
        yearlyRevenueRange: undefined,
        linkedinIndustryCategory: undefined,
        regionName: undefined,
        cityName: undefined,
        countryName: undefined,
      },
      companyTerritory: data.companyTerritory as Region[] | undefined,
      currentStep: 1,
    })

    navigate({ to: '/onboarding/email' })
  }, [updateState, navigate])

  useEffect(() => {
    if (!token)
      return

    joinInfoAppOnboardingJoinInfoGet({
      query: { token },
    })
      .then(({ data, error: respError }) => {
        if (respError || !data) {
          const detail = (respError as { detail?: string } | undefined)?.detail ?? 'unknown'
          setError(detail as ErrorState)
          return
        }
        hydrateAndRedirect(token, data)
      })
      .catch(() => {
        setError('unknown')
      })
  }, [token, hydrateAndRedirect])

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {error === 'already_verified' ? 'Already joined' : 'Invalid invite link'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              {error === 'token_expired' && 'This invite link has expired. Ask your admin to resend the invitation.'}
              {error === 'token_invalid' && 'This invite link is invalid. Ask your admin to resend the invitation.'}
              {error === 'already_verified' && 'You\'ve already set up your account.'}
              {error === 'no_token' && 'No invite token found. Check the link from your email.'}
              {error === 'unknown' && 'Something went wrong. Try again or ask your admin to resend the invitation.'}
            </p>
            {error === 'already_verified'
              ? (
                  <Button onClick={() => navigate({ to: '/login' })} className="w-full">
                    Go to login
                  </Button>
                )
              : (
                  <Button variant="outline" onClick={() => navigate({ to: '/login' })} className="w-full">
                    Back to login
                  </Button>
                )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-1 items-center justify-center py-16">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading your invitation...</span>
      </div>
    </div>
  )
}
